import { usePanelResize } from "@repo/ui/hooks/usePanelResize";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { useEffect, useRef, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useFullscreen } from "../../hooks/useFullscreen.js";
import { VideoOverlay } from "./videoOverlay.js";

interface RecordingVideoPanelProps {
  /** Presigned URL of the recording's source video, once it has loaded. */
  videoUrl?: string;
  /** Whether the request for the presigned media URL failed. */
  hasSourceError: boolean;
  /**
   * The shared audio player's media element for the active recording. The video
   * is slaved to it: because the recording's `audio.m4a` was extracted from this
   * same video, the two share a clock, so following the audio element's
   * play/pause/seek/rate keeps the picture in sync. The video is muted — the
   * audio comes from the shared player — so there's no double audio and the
   * existing control bar (scrub, rate, keyboard, transcript sync) stays the
   * single source of control.
   */
  nativeAudio: HTMLMediaElement | null;
  /** Kept by the owning transcript panel when its active recording changes. */
  height?: number;
  onHeightChange: (height: number | undefined) => void;
}

// Re-seek the video only when it drifts past this from the audio clock, so
// normal playback (both advancing at the same rate) doesn't stutter from
// constant currentTime writes.
const SYNC_DRIFT_THRESHOLD_S = 0.25;

/** Width cap shared by the video and the transcript column beneath it. */
export const TRANSCRIPT_COLUMN_MAX_WIDTH_CLASS = "max-w-[700px]";

interface VideoHeightLimits {
  min: number;
  max: number;
}

export function getVideoHeightLimits(
  renderedWidth?: number,
): VideoHeightLimits {
  const isNarrow = window.innerWidth < 768;
  const minHeight = isNarrow ? 120 : 180;
  const viewportMax = Math.floor(
    Math.min(
      isNarrow ? 240 : 720,
      window.innerHeight * (isNarrow ? 0.3 : 0.75),
    ),
  );
  const max = Math.min(
    Math.max(minHeight, viewportMax),
    renderedWidth ? (renderedWidth * 9) / 16 : Infinity,
  );
  return {
    min: Math.min(minHeight, max),
    max,
  };
}

export const RecordingVideoPanel: FC<RecordingVideoPanelProps> = (props) => {
  const { videoUrl, hasSourceError, nativeAudio } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, toggle: toggleFullscreen } =
    useFullscreen(fullscreenRef);
  const [heightLimits, setHeightLimits] = useState(() =>
    getVideoHeightLimits(),
  );
  // A stored height can fall outside the limits once the window resizes.
  // Without one the video sizes itself, to at most the upper limit.
  const height = Math.min(
    heightLimits.max,
    Math.max(heightLimits.min, props.height ?? heightLimits.max),
  );
  const { separatorProps, resizeOverlay } = usePanelResize({
    size: height,
    minSize: heightLimits.min,
    maxSize: heightLimits.max,
    onSizeChange: props.onHeightChange,
    edge: "bottom",
    label: "Resize video height",
    measureSize: () => videoRef.current?.getBoundingClientRect().height,
  });
  // The stored video's codec/container may not be playable in the browser (e.g.
  // .mkv, or HEVC) even though audio extraction + transcription succeeded. Show
  // a message instead of a blank black box when the element can't decode it.
  const [hasError, setHasError] = useState(false);
  const [hasLoadedFrame, setHasLoadedFrame] = useState(false);
  const [isFrameLoading, setIsFrameLoading] = useState(true);

  // Retry on a fresh source (e.g. a re-signed URL, or switching recordings).
  useEffect(() => {
    if (!videoUrl) {
      return;
    }
    setHasError(false);
    setHasLoadedFrame(false);
    setIsFrameLoading(true);
  }, [videoUrl]);

  const handleFrameLoaded = () => {
    setHasLoadedFrame(true);
    setIsFrameLoading(false);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    // The limits depend on the viewport and on the video's rendered width.
    const updateHeightLimits = () =>
      setHeightLimits((current) => {
        const next = getVideoHeightLimits(video.getBoundingClientRect().width);
        return current.min === next.min && current.max === next.max
          ? current
          : next;
      });
    const observer = new ResizeObserver(updateHeightLimits);
    observer.observe(video);
    window.addEventListener("resize", updateHeightLimits);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeightLimits);
    };
  }, [hasError, hasSourceError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl || !nativeAudio) {
      return;
    }

    const syncTime = () => {
      if (
        Math.abs(video.currentTime - nativeAudio.currentTime) >
        SYNC_DRIFT_THRESHOLD_S
      ) {
        video.currentTime = nativeAudio.currentTime;
      }
    };

    const onPlay = () => {
      syncTime();
      // Muted video, so programmatic play is allowed by autoplay policies.
      void video.play().catch(() => undefined);
    };
    const onPause = () => {
      video.pause();
      syncTime();
    };
    const onRateChange = () => {
      video.playbackRate = nativeAudio.playbackRate;
    };

    // Adopt the audio element's current state on mount / source change.
    video.playbackRate = nativeAudio.playbackRate;
    syncTime();
    if (!nativeAudio.paused) {
      void video.play().catch(() => undefined);
    }

    nativeAudio.addEventListener("play", onPlay);
    nativeAudio.addEventListener("pause", onPause);
    nativeAudio.addEventListener("seeking", syncTime);
    nativeAudio.addEventListener("timeupdate", syncTime);
    nativeAudio.addEventListener("ratechange", onRateChange);

    return () => {
      nativeAudio.removeEventListener("play", onPlay);
      nativeAudio.removeEventListener("pause", onPause);
      nativeAudio.removeEventListener("seeking", syncTime);
      nativeAudio.removeEventListener("timeupdate", syncTime);
      nativeAudio.removeEventListener("ratechange", onRateChange);
    };
    // `hasError` is a dep so that when the video errors out (and unmounts), this
    // effect re-runs and unbinds its listeners from the now-detached element.
  }, [nativeAudio, videoUrl, hasError]);

  return (
    <div className="flex flex-col items-center bg-white px-4 pt-4 pb-2">
      {hasSourceError ? (
        <div className="flex h-24 items-center px-4 text-center text-sm text-muted-foreground">
          This video could not be loaded. Refresh the page to try again.
        </div>
      ) : hasError ? (
        <div className="flex h-24 items-center px-4 text-center text-sm text-muted-foreground">
          This video cannot be played in the browser. The transcript and audio
          are still available.
        </div>
      ) : (
        <div
          ref={fullscreenRef}
          className={twMerge(
            "group relative flex w-full justify-center overflow-hidden rounded-lg bg-black",
            TRANSCRIPT_COLUMN_MAX_WIDTH_CLASS,
            isFullscreen && "rounded-none",
          )}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={() => {
              const video = videoRef.current;
              if (!video) return;

              // A small seek prompts the browser to decode the opening frame
              // while the shared audio player is still paused at the start.
              video.currentTime =
                nativeAudio?.currentTime ||
                Math.min(0.01, video.duration || 0.01);
            }}
            onLoadedData={handleFrameLoaded}
            onSeeking={() => setIsFrameLoading(true)}
            onSeeked={handleFrameLoaded}
            // `preload` is a hint. Browsers saving data (iOS Safari on cellular
            // or in Low Power Mode, data-saver modes) fetch nothing until play,
            // so no frame event ever arrives. `suspend` also follows a normal
            // metadata fetch, where the frame is still on its way.
            onSuspend={(event) => {
              if (
                event.currentTarget.readyState === HTMLMediaElement.HAVE_NOTHING
              ) {
                setIsFrameLoading(false);
              }
            }}
            onError={() => {
              setIsFrameLoading(false);
              setHasError(true);
            }}
            // No native controls: the shared audio player drives playback and
            // this element only mirrors it.
            className={twMerge(
              "aspect-video max-h-[65vh] w-full object-contain max-md:max-h-40",
              isFullscreen && "aspect-auto h-full max-h-none max-md:max-h-none",
            )}
            style={
              props.height !== undefined && !isFullscreen
                ? { height, maxHeight: heightLimits.max }
                : undefined
            }
          />
          {hasLoadedFrame || !isFrameLoading ? (
            <VideoOverlay
              isFullscreen={isFullscreen}
              isFrameLoading={isFrameLoading}
              onFullscreenToggle={toggleFullscreen}
            />
          ) : null}
          <div
            role="status"
            className={twMerge(
              "pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-muted opacity-100 transition-opacity duration-300",
              (hasLoadedFrame || !isFrameLoading) && "opacity-0",
            )}
          >
            {!hasLoadedFrame && isFrameLoading ? (
              <>
                <Loader style={LoaderStyle.SPINNER} className="size-6" />
                <span className="sr-only">Loading video...</span>
              </>
            ) : null}
          </div>
        </div>
      )}
      {!hasSourceError && !hasError ? (
        <div
          {...separatorProps}
          className="flex h-5 w-20 cursor-row-resize items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
          onDoubleClick={() => props.onHeightChange(undefined)}
        >
          <span
            aria-hidden
            className="h-1 w-12 rounded-full bg-border-strong"
          />
        </div>
      ) : null}
      {resizeOverlay}
    </div>
  );
};
